import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import type { Actions } from "./actions";

interface Item {
  id: string;
  cardId: string;
  cardType: string;
  preview: string | null;
  deletedAt: string;
  mine: boolean;
}

export function TrashPanel({
  boardId,
  actions,
  onClose,
}: {
  boardId: string;
  actions: Actions;
  onClose: () => void;
}) {
  const t = useT();
  const [items, setItems] = useState<Item[] | null>(null);
  const [tab, setTab] = useState<"mine" | "others">("mine");
  const load = useCallback(
    async () => setItems(await api<Item[]>(`/api/boards/${boardId}/trash`)),
    [boardId],
  );
  useEffect(() => {
    void load();
  }, [load]);
  const list = (items ?? []).filter((i) => (tab === "mine" ? i.mine : !i.mine));
  return (
    <aside className="absolute bottom-0 right-0 top-0 z-30 flex max-md:w-full md:w-80 flex-col border-l border-border bg-panel shadow-panel">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <span className="display text-xl">.{t.trash}</span>
        <button type="button" onClick={onClose} className="text-muted hover:text-text">
          ✕
        </button>
      </div>
      <div className="grid grid-cols-2 border-b border-border text-sm">
        <button
          type="button"
          onClick={() => setTab("mine")}
          className={`py-2 ${tab === "mine" ? "bg-accent text-bg" : "text-muted"}`}
        >
          {t.deletedByMe}
        </button>
        <button
          type="button"
          onClick={() => setTab("others")}
          className={`py-2 ${tab === "others" ? "bg-accent text-bg" : "text-muted"}`}
        >
          {t.deletedByOthers}
        </button>
      </div>
      <ul className="flex-1 overflow-auto">
        {items && list.length === 0 ? <li className="p-4 text-sm text-dim">—</li> : null}
        {list.map((i) => (
          <li key={i.id} className="flex items-center gap-2 border-b border-border px-4 py-2 text-sm">
            <span className="min-w-0 flex-1">
              <span className="block truncate">{i.preview || t.cardTypes[i.cardType] || i.cardType}</span>
              <span className="text-[11px] text-dim">
                {t.cardTypes[i.cardType] ?? i.cardType} · {new Date(i.deletedAt).toLocaleString()}
              </span>
            </span>
            <Button
              variant="ghost"
              className="px-2 py-1 text-xs"
              onClick={async () => {
                await actions.restoreFromTrash(i.id);
                await load();
              }}
            >
              {t.restore}
            </Button>
            <button
              type="button"
              title={t.deleteForever}
              onClick={async () => (await api(`/api/trash/${i.id}`, { method: "DELETE" }), load())}
              className="text-dim hover:text-error"
            >
              ✕
            </button>
          </li>
        ))}
      </ul>
    </aside>
  );
}
